import { useEffect, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface MenuAction {
  label: string;
  hint?: string;
  icon?: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
  /** Destructive actions read red, the same as everywhere else. */
  tone?: 'default' | 'danger';
}

/**
 * A menu behind one button.
 *
 * The popover behaviour — close on an outside click, close on Escape — is the
 * same as `ExportMenu`'s and lives here so a second menu cannot end up with
 * only half of it. Screens compose this rather than hand-rolling a dropdown.
 */
export function MenuButton({
  actions,
  label,
  icon: Icon = MoreHorizontal,
  align = 'right',
  className,
  triggerClassName,
}: {
  actions: MenuAction[];
  /** Omitted gives the icon-only ⋯ trigger; `aria-label` falls back to "More actions". */
  label?: string;
  icon?: LucideIcon;
  align?: 'left' | 'right';
  className?: string;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (actions.length === 0) return null;

  return (
    <div ref={container} className={cn('relative', className)}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label ?? 'More actions'}
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-body transition-colors hover:bg-cloud',
          triggerClassName,
        )}
      >
        <Icon aria-hidden className="h-4 w-4" />
        {label}
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute z-30 mt-1.5 w-56 animate-fade-in overflow-hidden rounded-xl border border-line bg-white shadow-flyout',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {actions.map((action) => (
            <MenuRow key={action.label} action={action} onDone={() => setOpen(false)} />
          ))}
        </div>
      )}
    </div>
  );
}

function MenuRow({ action, onDone }: { action: MenuAction; onDone: () => void }): ReactNode {
  const Icon = action.icon;

  return (
    <button
      type="button"
      role="menuitem"
      disabled={action.disabled}
      onClick={() => {
        onDone();
        action.onSelect();
      }}
      className={cn(
        'flex w-full items-start gap-2.5 px-3.5 py-2.5 text-left transition-colors hover:bg-cloud disabled:cursor-not-allowed disabled:opacity-50',
        action.tone === 'danger' && 'text-danger-600',
      )}
    >
      {Icon && <Icon aria-hidden className={cn('mt-0.5 h-4 w-4 shrink-0', action.tone === 'danger' ? 'text-danger-600' : 'text-muted')} />}
      <span className="min-w-0">
        <span className={cn('block text-sm font-medium', action.tone === 'danger' ? 'text-danger-600' : 'text-body')}>
          {action.label}
        </span>
        {action.hint && <span className="block truncate text-helper text-muted">{action.hint}</span>}
      </span>
    </button>
  );
}
